import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { queryKeys } from '../lib/queryClient';
import { snakeToCamel, camelToSnake } from '../lib/utils';
import { agentPhotoUrl } from '../lib/userColors';

function mapFromDb(row) { return snakeToCamel(row); }
function mapToDb(obj) { return camelToSnake(obj); }

export function useContacts({ enabled = true } = {}) {
  return useQuery({
    queryKey: queryKeys.contacts.all,
    queryFn: async () => {
      const data = await api.get('/contacts?limit=5000');
      return data.map(mapFromDb);
    },
    enabled,
  });
}

// People badge count. Keyed under contacts.all so every contact write and
// realtime contacts event refreshes it along with the list.
export function useIncompleteContactCount({ enabled = true } = {}) {
  return useQuery({
    queryKey: [...queryKeys.contacts.all, 'incomplete-count'],
    queryFn: async () => (await api.get('/contacts/incomplete-count')).count,
    enabled,
  });
}

export function useContact(id) {
  return useQuery({
    queryKey: queryKeys.contacts.detail(id),
    queryFn: async () => {
      const data = await api.get(`/contacts/${id}`);
      return mapFromDb(data);
    },
    enabled: !!id,
  });
}

export function useCreateContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (newContact) => {
      const data = await api.post('/contacts', mapToDb(newContact));
      return mapFromDb(data);
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: queryKeys.contacts.all });
      // Audit row is written server-side with a before/after diff
      // (server/routes/crud.js) — logging here as well produced two rows per save.
    },
  });
}

export function useUpdateContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (contact) => {
      const { id, ...updates } = contact;
      // Auto-set updated_at timestamp
      updates.updatedAt = new Date().toISOString();
      const data = await api.put(`/contacts/${id}`, mapToDb(updates));
      return mapFromDb(data);
    },
    onSuccess: async (data, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.contacts.all });
      qc.invalidateQueries({ queryKey: queryKeys.contacts.detail(data.id) });

      // Only cascade to jobs when name/email/phone actually changed
      const hasContactFieldChanges = variables.name !== undefined || variables.email !== undefined || variables.phone !== undefined;
      if (!hasContactFieldChanges) return;

      try {
        const jobs = await api.get(`/jobs?contact_id=${data.id}`);
        if (jobs.length) {
          await Promise.all(jobs.map(job =>
            api.put(`/jobs/${job.id}`, {
              client: data.name || '',
              contact: data.name || '',
              email: data.email || '',
              phone: data.phone || '',
            })
          ));
          qc.invalidateQueries({ queryKey: queryKeys.jobs.all });
        }
      } catch (err) {
        console.warn('Contact cascade to jobs failed:', err.message);
      }

      // Quotes hold client_name as free text too. Drafts follow the rename;
      // sent/accepted/superseded ones stay frozen as they were delivered, so
      // the portal still matches the PDF the client is holding.
      try {
        // api.get/api.put do no case translation — rows come back snake_case
        // and the CRUD whitelist matches snake_case exactly, so both ends must
        // be mapped explicitly (a camelCase body silently 400s as "no valid
        // columns"). The jobs cascade above only survives this because every
        // key it sends is a single word.
        const quotes = (await api.get(`/quotes?contact_id=${data.id}`)).map(mapFromDb);
        const drafts = quotes.filter((q) => q.status === 'draft');
        if (drafts.length) {
          // Name only unless the contact actually has an email — sending an
          // empty one would blank a client_email the quote set independently.
          const patch = { clientName: data.name || '' };
          if (data.email) patch.clientEmail = data.email;
          await Promise.all(drafts.map(q => api.put(`/quotes/${q.id}`, mapToDb(patch))));
          qc.invalidateQueries({ queryKey: queryKeys.quotes.all });
        }
      } catch (err) {
        console.warn('Contact cascade to draft quotes failed:', err.message);
      }

    },
  });
}

export function useConvertToClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (contact) => {
      const newData = { ...contact.data, outreach_status: 'client' };
      // Keep the RE-agent headshot after conversion (UserAvatar only falls back to it for prospects)
      if (!newData.avatar_url) newData.avatar_url = agentPhotoUrl(contact.name);
      const updates = { type: 'client', data: newData, updatedAt: new Date().toISOString() };
      const data = await api.put(`/contacts/${contact.id}`, mapToDb(updates));
      return mapFromDb(data);
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: queryKeys.contacts.all });
      qc.invalidateQueries({ queryKey: queryKeys.contacts.detail(data.id) });
    },
  });
}

export function useDeleteContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (contactId) => {
      await api.del(`/contacts/${contactId}`);
    },
    onSuccess: (_data, contactId) => {
      qc.invalidateQueries({ queryKey: queryKeys.contacts.all });
    },
  });
}
